import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { InlineLoading, InlineNotification } from '@carbon/react';
import { Helmet } from 'react-helmet-async';
import { useAtom, useSetAtom } from 'jotai';
import { ScenarioForm } from './scenario-form';
import { fetchScenarioLoadableAtom, areaGeometriesAtom } from './atoms';
import { DEFAULT_FORM_VALUES, mapResponseToFormData } from './utils';
import { pageTitle } from 'utils/page-title';

/**
 * Page entry point for both create and edit modes.
 * Determines mode from URL params — if :id is present, it's edit mode.
 * @return {JSX.Element} The scenario form page
 */
export function DiscountRateScenarioFormPage(): JSX.Element {
  const { id } = useParams<{ id?: string }>();
  const [fetchState, triggerFetch] = useAtom(fetchScenarioLoadableAtom);
  const setGeometries = useSetAtom(areaGeometriesAtom);

  useEffect(() => {
    if (id) triggerFetch(Number(id));
  }, [id, triggerFetch]);

  const mapped = useMemo(() => {
    if (!id || fetchState.state !== 'hasData' || !fetchState.data) return null;
    return mapResponseToFormData(fetchState.data);
  }, [id, fetchState]);

  useEffect(() => {
    if (mapped?.geometries) {
      setGeometries(mapped.geometries);
    }
  }, [mapped, setGeometries]);

  if (!id) {
    return (
      <>
        <Helmet>
          <title>{pageTitle('New Discount Rate Scenario')}</title>
        </Helmet>
        <ScenarioForm initialValues={DEFAULT_FORM_VALUES} mode="create" />
      </>
    );
  }

  if (fetchState.state === 'hasError') {
    return (
      <InlineNotification
        kind="error"
        title="Failed to load scenario"
        hideCloseButton
        lowContrast
      />
    );
  }

  if (fetchState.state === 'loading' || !mapped) {
    return <InlineLoading description="Loading scenario..." />;
  }

  return (
    <>
      <Helmet>
        <title>
          {pageTitle(fetchState.data?.name || 'Discount Rate Scenario')}
        </title>
      </Helmet>
      <ScenarioForm
        initialValues={mapped.formData}
        mode="edit"
        scenarioId={id}
        commentsUrl={fetchState.data?.resources?.comments?.href}
        eventsUrl={fetchState.data?.resources?.events?.href}
      />
    </>
  );
}
